<template>
  <v-card flat class="border-0 !bg-[rgb(var(--v-theme-surface))] overflow-hidden">
    <!-- ═══ POST-GENERATION: New V2 Layout ═══ -->
    <template v-if="statementReady">
      <!-- Redesigned completed card (settlementRedesign) — cloned from prototype. -->
      <v-card-text v-if="useDisbursementTable" class="pa-4">
        <SettlementCompletePanel
          :generated-date="generatedDate"
          :download-url="downloadUrl"
          :can-reset="canReset"
          :reset-blocked-reason="resetBlockedReason"
          :invoice-finalized="invoiceFinalized"
          :trust-statement-loading="trustStatementLoading"
          @unlock-and-reset="$emit('unlock-and-reset')"
          @download="$emit('download')"
          @download-trust-statement="$emit('download-trust-statement')"
          @view-invoice="$emit('view-invoice')"
        />
      </v-card-text>

      <v-card-text v-else class="pa-0">
        <div
          class="d-flex align-center justify-space-between flex-wrap gap-2 px-4 py-3 bg-green-lighten-5 border-b border-green-lighten-3"
          data-cy="statement-ready-alert"
        >
          <div class="d-flex align-center gap-2 flex-wrap">
            <span
              class="d-inline-flex align-center gap-1 font-weight-bold bg-green-lighten-5 text-green-darken-3 px-3 py-1 rounded-pill text-body-2"
            >
              <LlIcon icon="mdi-check" color="success" size="14" />
              Settlement Complete
            </span>
            <span class="text-grey-darken-1 text-body-2">
              Generated {{ generatedDateFormatted }}
            </span>
          </div>

          <div class="d-flex align-center gap-2 flex-wrap">
            <a
              v-if="downloadUrl"
              :href="downloadUrl"
              download="settlement-statement.pdf"
              class="text-decoration-none"
              data-cy="download-pdf-link"
              @click="$emit('download')"
            >
              <LlSecondaryButton prepend-icon="mdi-download" size="small">
                Download PDF
              </LlSecondaryButton>
            </a>
            <LlTooltip v-if="!canReset" location="top">
              <div class="text-center whitespace-pre-line">
                {{ resetBlockedReason || 'Reset is not available' }}
              </div>
              <template #activator>
                <LlSecondaryButton
                  color="error"
                  variant="text"
                  prepend-icon="mdi-lock"
                  size="small"
                  data-cy="unlock-reset-button"
                  disabled
                >
                  Unlock &amp; Reset
                </LlSecondaryButton>
              </template>
            </LlTooltip>
            <LlSecondaryButton
              v-else
              color="error"
              variant="text"
              prepend-icon="mdi-lock-open"
              size="small"
              data-cy="unlock-reset-button"
              @click="$emit('unlock-and-reset')"
            >
              Unlock &amp; Reset
            </LlSecondaryButton>
          </div>
        </div>

        <template v-if="showPostSettlementActions">
          <div
            class="d-flex align-center gap-3 px-4 pt-3 pb-2 border-b border-grey-lighten-3 text-grey-darken-1 text-body-2 font-weight-medium"
          >
            Post-Settlement Actions
            <span
              v-if="stageBadge"
              class="text-caption font-weight-medium text-blue-darken-2 bg-blue-lighten-5 px-2 py-[2px] rounded-pill"
            >
              &#10003; Stage &rarr; {{ stageBadge }}
            </span>
          </div>

          <SettlementPostActions
            :invoice-finalized="invoiceFinalized"
            :can-finalize-invoice="canFinalizeInvoice"
            :can-undo-invoice="canUndoInvoice"
            :invoice-blocked-reason="invoiceBlockedReason"
            :trust-paid="trustPaid"
            :can-pay-from-trust="canPayFromTrust"
            :can-undo-trust-payment="canUndoTrustPayment"
            :trust-payment-blocked-reason="trustPaymentBlockedReason"
            :undo-trust-payment-blocked-reason="undoTrustPaymentBlockedReason"
            :qbo-pushed="qboPushed"
            :can-push-client-disbursement="canPushClientDisbursement"
            :can-undo-client-disbursement="canUndoClientDisbursement"
            :disbursement-blocked-reason="disbursementBlockedReason"
            :undo-disbursement-blocked-reason="undoDisbursementBlockedReason"
            :invoice-amount="invoiceAmount"
            :net-to-client="netToClient"
            :trust-balance="trustBalance"
            :trust-balance-loading="trustBalanceLoading"
            :trust-balance-error="trustBalanceError"
            :client-accounting-enabled="clientAccountingEnabled"
            :trust-insufficient="trustInsufficient"
            :saving="postSaving"
            :saving-column="savingColumn"
            :error="postError"
            :column-error="postColumnError"
            :stage-badge="stageBadge"
            @finalize-invoice="$emit('finalize-invoice')"
            @undo-invoice="$emit('undo-invoice')"
            @pay-trust="$emit('pay-trust')"
            @undo-trust="$emit('undo-trust')"
            @push-qbo="$emit('push-qbo')"
            @undo-qbo="$emit('undo-qbo')"
            @view-invoice="$emit('view-invoice')"
          />
        </template>

        <div
          class="d-flex align-center gap-4 px-4 py-2 flex-wrap bg-grey-lighten-5 border-t border-grey-lighten-3"
        >
          <a
            class="d-flex align-center gap-1 text-decoration-none text-grey-darken-2 text-body-2"
            :class="downloadUrl ? 'cursor-pointer' : 'cursor-not-allowed opacity-60 pointer-events-none'"
            :href="downloadUrl || undefined"
            download="settlement-statement.pdf"
            data-cy="settlement-statement-link"
            :aria-disabled="!downloadUrl"
            @click="downloadUrl && $emit('download')"
          >
            <v-icon size="15">mdi-file-document-outline</v-icon>
            Settlement Statement
          </a>
          <a
            v-if="invoiceFinalized"
            class="d-flex align-center gap-1 text-decoration-none text-grey-darken-2 text-body-2"
            :class="[
              trustStatementLoading ? 'cursor-not-allowed opacity-60 pointer-events-none' : 'cursor-pointer',
            ]"
            data-cy="trust-statement-link"
            :aria-disabled="trustStatementLoading"
            @click.prevent="!trustStatementLoading && $emit('download-trust-statement')"
          >
            <v-icon size="15">mdi-file-document-outline</v-icon>
            {{ trustStatementLoading ? 'Downloading Trust Statement...' : 'Trust Statement' }}
            <LlTooltip location="top">
              <div class="max-w-[260px]">
                A summary of all trust account activity associated with this settlement.
              </div>
              <template #activator>
                <v-icon size="13" class="cursor-pointer text-grey">mdi-information-outline</v-icon>
              </template>
            </LlTooltip>
          </a>
        </div>
      </v-card-text>
    </template>

    <!-- ═══ POLLING / GENERATING: Waiting for PDF ═══ -->
    <template v-else-if="generating || polling">
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Step {{ stepNumber }}: Generate Settlement Statement</v-card-title
      >
      <v-card-text>
        <div
          class="flex flex-col items-center py-8 gap-4"
          data-cy="polling-state"
        >
          <v-progress-circular indeterminate color="success" size="48" />
          <div class="text-body-1 font-weight-medium">
            Generating settlement statement…
          </div>
          <div class="text-caption text-grey">
            This usually takes a few seconds. Please wait.
          </div>
        </div>
      </v-card-text>
    </template>

    <!-- ═══ TIMED OUT ═══ -->
    <template v-else-if="timedOut">
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Step {{ stepNumber }}: Generate Settlement Statement</v-card-title
      >
      <v-card-text>
        <AlertBar class="mb-4" data-cy="timeout-alert">
          <div class="font-weight-bold mb-1">Still generating…</div>
          <p class="mb-2 text-body-2">
            The statement is taking longer than expected. The PDF may still be processing.
          </p>
          <LlSecondaryButton
            color="warning"
            :loading="saving"
            data-cy="retry-polling-button"
            @click="$emit('retry-polling')"
          >
            Check again
          </LlSecondaryButton>
        </AlertBar>
      </v-card-text>
    </template>

    <!-- ═══ PRE-GENERATION: Ready to generate ═══ -->
    <template v-else>
      <v-card-title class="text-h6 font-weight-bold pb-0"
        >Step {{ stepNumber }}: Generate Settlement Statement</v-card-title
      >
      <v-card-text>
        <AlertBar v-if="!canGenerate" class="mb-4" data-cy="cannot-generate-alert">
          All prior steps (1–5) must be completed before you can generate the settlement statement.
        </AlertBar>

        <AlertBar type="info" class="mb-4" data-cy="generation-info-alert">
          Generating the statement will lock the settlement and produce a PDF. You can unlock and reset afterwards if changes are needed.
        </AlertBar>

        <div v-if="previewGenerating || previewPolling" class="flex flex-col items-center py-8 gap-4" data-cy="preview-loading-state">
          <v-progress-circular indeterminate color="blue" size="48" />
          <div class="text-body-1 font-weight-medium">Generating statement preview…</div>
          <div class="text-caption text-grey">This usually takes a few seconds.</div>
        </div>

        <AlertBar v-else-if="previewTimedOut" class="mb-4" data-cy="preview-timeout-alert">
          <div class="font-weight-bold mb-1">Preview taking longer than expected…</div>
          <p class="mb-2 text-body-2">The preview PDF is still being generated.</p>
          <LlSecondaryButton color="warning" data-cy="retry-preview-button" @click="$emit('retry-preview')">
            Try again
          </LlSecondaryButton>
        </AlertBar>

        <div
          v-else-if="previewUrl"
          class="w-full border rounded mb-4 overflow-auto max-h-[600px]"
          data-cy="statement-preview-pre"
        >
          <VuePdfEmbed :source="previewUrl" />
        </div>

        <div class="flex gap-3 mt-2">
          <LlSecondaryButton @click="$emit('go-back')" data-cy="back-button">
            &larr; Back
          </LlSecondaryButton>
          <LlSecondaryButton
            v-if="canGenerate"
            color="primary"
            :loading="saving"
            data-cy="generate-button"
            @click="$emit('generate')"
          >
            Generate Statement
          </LlSecondaryButton>
        </div>
      </v-card-text>
    </template>
  </v-card>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import AlertBar from '@/components/AlertBar.vue';
import LlTooltip from '@/components/LlTooltip.vue';
import LlIcon from '@/components/LlIcon.vue';
import SettlementPostActions from './SettlementPostActions.vue';
import SettlementCompletePanel from './SettlementCompletePanel.vue';
import { formatLongDateString } from '@/services/utils';
import { VuePdfEmbed } from '@/utils/pdfHelpers';

const props = defineProps({
  /** Number shown in the title — the parent renumbers when Map Payments is skipped. */
  stepNumber: { type: Number, default: 6 },
  stageUpdated: { type: Boolean, default: false },
  // ── Shared with V1 ────────────────────────────────────
  statementReady: { type: Boolean, default: false },
  polling: { type: Boolean, default: false },
  timedOut: { type: Boolean, default: false },
  generating: { type: Boolean, default: false },
  saving: { type: Boolean, default: false },
  downloadUrl: { type: String, default: null },
  generatedDate: { type: String, default: null },
  canGenerate: { type: Boolean, default: true },
  previewUrl: { type: String, default: null },
  previewGenerating: { type: Boolean, default: false },
  previewPolling: { type: Boolean, default: false },
  previewTimedOut: { type: Boolean, default: false },
  canReset: { type: Boolean, default: true },
  resetBlockedReason: { type: String, default: null },
  /** Parent-provided feature flag for the post-settlement actions panel. */
  showPostSettlementActions: { type: Boolean, required: true },
  /**
   * When true (settlementRedesign path), render the redesigned disbursement
   * table instead of the 3-column post-settlement actions. The table is
   * store-connected, so it needs none of the post-settlement props below.
   */
  useDisbursementTable: { type: Boolean, default: false },

  // ── Post-Settlement Actions ────────────────────────────
  // NOTE: the props below are consumed ONLY by the legacy 3-column
  // SettlementPostActions (rendered when `useDisbursementTable` is false — i.e.
  // the settlementRedesign fallback). The redesign path's SettlementCompletePanel
  // is store-connected and ignores them. They intentionally stay for the fallback;
  // remove this block when the settlementRedesign flag is retired.
  invoiceFinalized: { type: Boolean, default: false },
  canFinalizeInvoice: { type: Boolean, default: false },
  canUndoInvoice: { type: Boolean, default: false },
  invoiceBlockedReason: { type: String, default: null },
  trustPaid: { type: Boolean, default: false },
  canPayFromTrust: { type: Boolean, default: false },
  canUndoTrustPayment: { type: Boolean, default: false },
  trustPaymentBlockedReason: { type: String, default: null },
  undoTrustPaymentBlockedReason: { type: String, default: null },
  qboPushed: { type: Boolean, default: false },
  canPushClientDisbursement: { type: Boolean, default: false },
  canUndoClientDisbursement: { type: Boolean, default: false },
  disbursementBlockedReason: { type: String, default: null },
  undoDisbursementBlockedReason: { type: String, default: null },
  invoiceAmount: { type: Number, default: null },
  netToClient: { type: Number, default: null },
  trustBalance: { type: Number, default: null },
  trustBalanceLoading: { type: Boolean, default: false },
  trustBalanceError: { type: Boolean, default: false },
  /** SC-46858 flipper — forwarded to SettlementPostActions for its trust tooltip copy. */
  clientAccountingEnabled: { type: Boolean, default: false },
  trustInsufficient: { type: Boolean, default: false },
  postSaving: { type: Boolean, default: false },
  postError: { type: String, default: null },
  postColumnError: { type: String, default: null },
  savingColumn: { type: String, default: null },
  trustStatementLoading: { type: Boolean, default: false },
});

defineEmits([
  'retry-polling',
  'unlock-and-reset',
  'download',
  'go-back',
  'generate',
  'retry-preview',
  'download-trust-statement',
  // Post-settlement
  'finalize-invoice',
  'undo-invoice',
  'pay-trust',
  'undo-trust',
  'push-qbo',
  'undo-qbo',
  'view-invoice',
]);

const generatedDateFormatted = computed(() => {
  if (!props.generatedDate) return '';
  return formatLongDateString(props.generatedDate) || props.generatedDate;
});

const stageBadge = computed(() =>
  props.stageUpdated ? 'Settlement Pending' : null,
);
</script>
